import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Image,
  Platform,
  PanResponder,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "../ThemedText";
import { ThemedToolTip } from "../ThemedToolTip";
import { useThemeColor } from "@/hooks/useThemeColor";
import { MediaSize } from "./types";
import { resolveMediaDimensions } from "./html-sanitizer";

export interface IThemedMediaEmbedProps {
  id: string;
  title: string;
  mediaType?: "image" | "video";
  size?: MediaSize;
  width?: number | string;
  height?: number | string;
  align?: "left" | "right" | "center";
  downloadUrl?: string;
  editable?: boolean;
  draggable?: boolean;
  onSizeChange?: (size: MediaSize) => void;
  onWidthChange?: (width: number | string) => void;
  onHeightChange?: (height: number) => void;
  onAlignChange?: (align: "left" | "right" | "center") => void;
  onRemove?: () => void;
  onPress?: () => void;
  onDragStart?: (e: any) => void;
  onDragEnd?: (e: any) => void;
}

const PRESET_SIZES: { label: string; value: MediaSize; iconName: any }[] = [
  { label: "25%", value: "25%", iconName: "contract-outline" },
  { label: "50%", value: "50%", iconName: "browsers-outline" },
  { label: "75%", value: "75%", iconName: "scan-outline" },
  { label: "100%", value: "100%", iconName: "expand-outline" },
];

const PRESET_ALIGNS: { label: string; value: "left" | "right" | "center"; tooltip: string }[] = [
  { label: "Left", value: "left", tooltip: "Float left (text wraps right)" },
  { label: "Center", value: "center", tooltip: "Center (break text above and below)" },
  { label: "Right", value: "right", tooltip: "Float right (text wraps left)" },
];

/**
 * Parses a MediaSize percentage string into a clamped numeric percentage (20 to 100).
 */
export function parseMediaSizePercent(val?: string): number {
  if (!val) return 50;
  const match = String(val).match(/^(\d+(?:\.\d+)?)%$/);
  if (match) {
    const num = parseFloat(match[1]);
    return Math.min(100, Math.max(20, Math.round(num)));
  }
  if (val === "25%") return 25;
  if (val === "75%") return 75;
  if (val === "100%") return 100;
  return 50;
}

/**
 * Parses a numeric media height clamped between 80px and 800px (default 180px).
 */
export function parseMediaHeight(val?: number | string): number {
  if (val === undefined || val === null || val === "") return 180;
  const num = typeof val === "number" ? val : parseInt(String(val).replace(/[^\d]/g, ""), 10);
  if (isNaN(num)) return 180;
  return Math.min(800, Math.max(80, Math.round(num)));
}

/**
 * Interactive In-Place Media Embed Component for ThemedEditor.
 *
 * Provides visual rendering for images and videos with:
 * - Interactive draggable sizing handles on the border and corner to expand or contract.
 * - Live real-time width updating and floating resize percentage feedback.
 * - One-tap quick snap preset buttons (25%, 50%, 75%, 100%).
 * - Zero-keyboard removal and video badge preview.
 */
export const ThemedMediaEmbed: React.FC<IThemedMediaEmbedProps> = ({
  id,
  title,
  mediaType = "image",
  size = "50%",
  width,
  height,
  align = "left",
  downloadUrl,
  editable = true,
  draggable = false,
  onSizeChange,
  onWidthChange,
  onHeightChange,
  onAlignChange,
  onRemove,
  onPress,
  onDragStart,
  onDragEnd,
}) => {
  const resolved = useMemo(
    () => resolveMediaDimensions({ size, width, height }),
    [size, width, height]
  );
  const [isHovered, setIsHovered] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [parentWidth, setParentWidth] = useState<number>(600);
  const [currentPercent, setCurrentPercent] = useState<number>(() => parseMediaSizePercent(resolved.size));
  const [currentHeight, setCurrentHeight] = useState<number>(() => resolved.height);
  const [currentWidth, setCurrentWidth] = useState<number | string>(() => resolved.width);

  const startPercentRef = useRef<number>(currentPercent);
  const startHeightRef = useRef<number>(currentHeight);

  // Sync external size, width, and height prop changes
  useEffect(() => {
    if (!isResizing) {
      const res = resolveMediaDimensions({ size, width, height });
      setCurrentPercent(parseMediaSizePercent(res.size));
      setCurrentHeight(res.height);
      setCurrentWidth(res.width);
    }
  }, [size, width, height, isResizing]);

  // Theme tokens
  const borderColor = useThemeColor({}, "border");
  const cardBg = useThemeColor({}, "card");
  const textColor = useThemeColor({}, "text");
  const tintColor = useThemeColor({}, "tint");
  const isDark = cardBg === "#131520";

  const isVideo = mediaType === "video" || title.toLowerCase().match(/\.(mp4|mov|avi|webm)$/);

  // Handle live resizing delta (dx for width %, dy for height px)
  const handleResizeMove = useCallback((dx: number, dy: number = 0) => {
    const pWidth = parentWidth > 0 ? parentWidth : 600;
    const deltaPercent = (dx / pWidth) * 100;
    const nextPercent = Math.min(100, Math.max(20, Math.round(startPercentRef.current + deltaPercent)));
    setCurrentPercent(nextPercent);
    if (dy !== 0) {
      const nextHeight = Math.min(800, Math.max(80, Math.round(startHeightRef.current + dy)));
      setCurrentHeight(nextHeight);
    }
  }, [parentWidth]);

  // Handle end of resize gesture
  const handleResizeEnd = useCallback((dx: number, dy: number = 0) => {
    setIsResizing(false);
    const pWidth = parentWidth > 0 ? parentWidth : 600;
    const deltaPercent = (dx / pWidth) * 100;
    const rawPercent = Math.min(100, Math.max(20, Math.round(startPercentRef.current + deltaPercent)));

    // Snap to standard presets if within 3% tolerance
    let finalSize: MediaSize = `${rawPercent}%`;
    if (Math.abs(rawPercent - 25) <= 3) finalSize = "25%";
    else if (Math.abs(rawPercent - 50) <= 3) finalSize = "50%";
    else if (Math.abs(rawPercent - 75) <= 3) finalSize = "75%";
    else if (Math.abs(rawPercent - 100) <= 3) finalSize = "100%";

    setCurrentPercent(parseMediaSizePercent(finalSize));
    setCurrentWidth(finalSize);
    onSizeChange?.(finalSize);
    onWidthChange?.(finalSize);

    if (dy !== 0) {
      const finalHeight = Math.min(800, Math.max(80, Math.round(startHeightRef.current + dy)));
      setCurrentHeight(finalHeight);
      onHeightChange?.(finalHeight);
    }
  }, [parentWidth, onSizeChange, onHeightChange]);

  // PanResponder for right border handle (horizontal width)
  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => !!editable,
        onMoveShouldSetPanResponder: () => !!editable,
        onPanResponderGrant: () => {
          setIsResizing(true);
          startPercentRef.current = currentPercent;
          startHeightRef.current = currentHeight;
        },
        onPanResponderMove: (_evt, gestureState) => {
          handleResizeMove(gestureState.dx, 0);
        },
        onPanResponderRelease: (_evt, gestureState) => {
          handleResizeEnd(gestureState.dx, 0);
        },
        onPanResponderTerminate: (_evt, gestureState) => {
          handleResizeEnd(gestureState.dx, 0);
        },
      }),
    [editable, currentPercent, currentHeight, handleResizeMove, handleResizeEnd]
  );

  // PanResponder for bottom border handle (vertical height)
  const bottomPanResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => !!editable,
        onMoveShouldSetPanResponder: () => !!editable,
        onPanResponderGrant: () => {
          setIsResizing(true);
          startPercentRef.current = currentPercent;
          startHeightRef.current = currentHeight;
        },
        onPanResponderMove: (_evt, gestureState) => {
          handleResizeMove(0, gestureState.dy);
        },
        onPanResponderRelease: (_evt, gestureState) => {
          handleResizeEnd(0, gestureState.dy);
        },
        onPanResponderTerminate: (_evt, gestureState) => {
          handleResizeEnd(0, gestureState.dy);
        },
      }),
    [editable, currentPercent, currentHeight, handleResizeMove, handleResizeEnd]
  );

  // PanResponder for bottom-right corner handle (2D diagonal resizing)
  const cornerPanResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => !!editable,
        onMoveShouldSetPanResponder: () => !!editable,
        onPanResponderGrant: () => {
          setIsResizing(true);
          startPercentRef.current = currentPercent;
          startHeightRef.current = currentHeight;
        },
        onPanResponderMove: (_evt, gestureState) => {
          handleResizeMove(gestureState.dx, gestureState.dy);
        },
        onPanResponderRelease: (_evt, gestureState) => {
          handleResizeEnd(gestureState.dx, gestureState.dy);
        },
        onPanResponderTerminate: (_evt, gestureState) => {
          handleResizeEnd(gestureState.dx, gestureState.dy);
        },
      }),
    [editable, currentPercent, currentHeight, handleResizeMove, handleResizeEnd]
  );

  // Web mouse down handler for fluid browser desktop drag (supports x, y, or both)
  const handleMouseDown = useCallback((e: any, axis: "x" | "y" | "both" = "x") => {
    if (Platform.OS !== "web" || !editable) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startPercentRef.current = currentPercent;
    startHeightRef.current = currentHeight;
    const startX = e.clientX;
    const startY = e.clientY;

    const onMouseMove = (moveEvent: any) => {
      const dx = axis === "y" ? 0 : moveEvent.clientX - startX;
      const dy = axis === "x" ? 0 : moveEvent.clientY - startY;
      handleResizeMove(dx, dy);
    };

    const onMouseUp = (upEvent: any) => {
      if (typeof window !== "undefined") {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      }
      const dx = axis === "y" ? 0 : upEvent.clientX - startX;
      const dy = axis === "x" ? 0 : upEvent.clientY - startY;
      handleResizeEnd(dx, dy);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    }
  }, [editable, currentPercent, currentHeight, handleResizeMove, handleResizeEnd]);

  // Check if current percent exactly matches a standard preset
  const matchingPreset = PRESET_SIZES.find((p) => parseMediaSizePercent(p.value) === currentPercent);

  return (
    <View
      style={[
        styles.fullWidthWrapper,
        (align === "left" || align === "right") && {
          width: "100%",
          marginVertical: 0,
          alignItems: align === "right" ? "flex-end" : "flex-start",
        },
      ]}
      onLayout={(e) => {
        const w = e.nativeEvent.layout.width;
        if (w > 0) setParentWidth(w);
      }}
    >
      <View
        style={[
          styles.outerContainer,
          {
            width: align === "left" || align === "right" ? "100%" : (`${currentPercent}%` as any),
            minWidth: 140,
          },
        ]}
        {...(Platform.OS === "web"
          ? {
              onMouseEnter: () => setIsHovered(true),
              onMouseLeave: () => setIsHovered(false),
              draggable: !!draggable,
              onDragStart,
              onDragEnd,
            }
          : {})}
      >
        {/* Sizing Control Bar in Edit Mode */}
        {editable && (
          <View style={[styles.sizingBar, { backgroundColor: cardBg, borderColor }]}>
            <View style={styles.sizingLabelRow}>
              <Ionicons
                name={isVideo ? "videocam" : "image"}
                size={13}
                color={tintColor}
                style={{ marginRight: 4 }}
              />
              <ThemedText style={styles.sizingBarTitle} numberOfLines={1}>
                {title || "Media"}
              </ThemedText>
            </View>

            <View style={styles.sizeOptionsRow}>
              {PRESET_SIZES.map((s) => {
                const isSelected = matchingPreset?.value === s.value;
                return (
                  <ThemedToolTip key={s.value} text={`Resize to ${s.value} width`}>
                    <TouchableOpacity
                      onPress={() => {
                        const newPct = parseMediaSizePercent(s.value);
                        setCurrentPercent(newPct);
                        onSizeChange?.(s.value);
                      }}
                      style={[
                        styles.sizeBtn,
                        { borderColor },
                        isSelected && [styles.sizeBtnActive, { backgroundColor: tintColor, borderColor: tintColor }],
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel={`Set size to ${s.value}`}
                    >
                      <ThemedText
                        style={[
                          styles.sizeBtnText,
                          { color: isSelected ? "#FFFFFF" : textColor },
                        ]}
                      >
                        {s.label}
                      </ThemedText>
                    </TouchableOpacity>
                  </ThemedToolTip>
                );
              })}

              {!matchingPreset && (
                <View
                  style={[
                    styles.sizeBtn,
                    styles.sizeBtnActive,
                    { backgroundColor: tintColor, borderColor: tintColor },
                  ]}
                >
                  <ThemedText style={[styles.sizeBtnText, { color: "#FFFFFF" }]}>
                    {currentPercent}%
                  </ThemedText>
                </View>
              )}

              {/* In-place Alignment Controls (Float Left, Center, Float Right) */}
              <View style={styles.alignOptionsRow}>
                {PRESET_ALIGNS.map((a) => {
                  const isSelected = (align || "left") === a.value;
                  return (
                    <ThemedToolTip key={a.value} text={a.tooltip}>
                      <TouchableOpacity
                        onPress={() => onAlignChange?.(a.value)}
                        style={[
                          styles.sizeBtn,
                          { borderColor },
                          isSelected && [styles.sizeBtnActive, { backgroundColor: tintColor, borderColor: tintColor }],
                        ]}
                        accessibilityRole="button"
                        accessibilityLabel={`Set alignment to ${a.value}`}
                      >
                        <ThemedText
                          style={[
                            styles.sizeBtnText,
                            { color: isSelected ? "#FFFFFF" : textColor },
                          ]}
                        >
                          {a.label}
                        </ThemedText>
                      </TouchableOpacity>
                    </ThemedToolTip>
                  );
                })}
              </View>

              {onRemove && (
                <ThemedToolTip text="Remove this media">
                  <TouchableOpacity
                    onPress={onRemove}
                    style={styles.removeMediaBtn}
                    accessibilityRole="button"
                    accessibilityLabel="Remove media"
                  >
                    <Ionicons name="close" size={14} color="#EF4444" />
                  </TouchableOpacity>
                </ThemedToolTip>
              )}
            </View>
          </View>
        )}

        {/* Media Preview Box */}
        <TouchableOpacity
          activeOpacity={editable ? 0.9 : 0.7}
          onPress={onPress}
          disabled={!onPress}
          style={[
            styles.previewContainer,
            {
              minHeight: currentHeight,
              borderColor: isResizing ? tintColor : isHovered && editable ? tintColor : borderColor,
              borderWidth: isResizing ? 2 : 1,
              backgroundColor: isDark ? "#1A1D2B" : "#F3F4F6",
            },
          ]}
        >
          {downloadUrl ? (
            <Image
              source={{ uri: downloadUrl }}
              style={[styles.mediaImage, { height: currentHeight }]}
              resizeMode="cover"
            />
          ) : (
            <View style={[styles.placeholderBox, { minHeight: currentHeight }]}>
              <Ionicons
                name={isVideo ? "film-outline" : "images-outline"}
                size={36}
                color={tintColor}
              />
              <ThemedText style={styles.placeholderTitle} numberOfLines={1}>
                {title || "Media Item"}
              </ThemedText>
              <ThemedText style={styles.placeholderSize}>Size: {currentPercent}% ({currentHeight}px)</ThemedText>
            </View>
          )}

          {/* Video Overlay Badge */}
          {isVideo && (
            <View style={styles.videoBadge}>
              <Ionicons name="play-circle" size={18} color="#FFFFFF" />
              <ThemedText style={styles.videoBadgeText}>VIDEO</ThemedText>
            </View>
          )}

          {/* Live Floating Sizing Badge while Dragging */}
          {isResizing && (
            <View style={[styles.resizeFloatingBadge, { backgroundColor: tintColor }]}>
              <Ionicons name="resize" size={11} color="#FFFFFF" style={{ marginRight: 3 }} />
              <ThemedText style={styles.resizeFloatingBadgeText}>
                {currentPercent}% × {currentHeight}px
              </ThemedText>
            </View>
          )}

          {/* DRAGGABLE RIGHT BORDER SIZING HANDLE */}
          {editable && (
            <View
              {...panResponder.panHandlers}
              {...(Platform.OS === "web" ? { onMouseDown: (e) => handleMouseDown(e, "x") } : {})}
              style={[
                styles.rightBorderHandle,
                (isHovered || isResizing) && styles.rightBorderHandleActive,
              ]}
              accessibilityRole="adjustable"
              accessibilityLabel="Drag right border to resize media width"
            >
              <View
                style={[
                  styles.borderGripPill,
                  {
                    backgroundColor: isResizing || isHovered ? tintColor : borderColor,
                  },
                ]}
              />
            </View>
          )}

          {/* DRAGGABLE BOTTOM BORDER SIZING HANDLE */}
          {editable && (
            <View
              {...bottomPanResponder.panHandlers}
              {...(Platform.OS === "web" ? { onMouseDown: (e) => handleMouseDown(e, "y") } : {})}
              style={[
                styles.bottomBorderHandle,
                (isHovered || isResizing) && styles.bottomBorderHandleActive,
              ]}
              accessibilityRole="adjustable"
              accessibilityLabel="Drag bottom border to resize media height"
            >
              <View
                style={[
                  styles.bottomGripPill,
                  {
                    backgroundColor: isResizing || isHovered ? tintColor : borderColor,
                  },
                ]}
              />
            </View>
          )}

          {/* DRAGGABLE BOTTOM-RIGHT CORNER SIZING HANDLE */}
          {editable && (
            <View
              {...cornerPanResponder.panHandlers}
              {...(Platform.OS === "web" ? { onMouseDown: (e) => handleMouseDown(e, "both") } : {})}
              style={[
                styles.cornerResizeHandle,
                {
                  backgroundColor: tintColor,
                  borderColor: cardBg,
                },
              ]}
              accessibilityRole="adjustable"
              accessibilityLabel="Drag corner to resize media"
            >
              <Ionicons name="resize" size={12} color="#FFFFFF" />
            </View>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  fullWidthWrapper: {
    width: "100%",
    marginVertical: 10,
    alignItems: "flex-start",
  },
  outerContainer: {
    alignSelf: "flex-start",
    position: "relative",
  },
  sizingBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 5,
    marginBottom: 6,
    flexWrap: "wrap",
    gap: 4,
  },
  sizingLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 1,
  },
  sizingBarTitle: {
    fontSize: 11,
    fontWeight: "700",
    maxWidth: 130,
  },
  sizeOptionsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  alignOptionsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  sizeBtn: {
    borderWidth: 1,
    borderRadius: 5,
    paddingHorizontal: 6,
    paddingVertical: 2.5,
  },
  sizeBtnActive: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 2,
  },
  sizeBtnText: {
    fontSize: 10,
    fontWeight: "700",
  },
  removeMediaBtn: {
    padding: 3,
    borderRadius: 4,
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    marginLeft: 2,
  },
  previewContainer: {
    borderRadius: 8,
    overflow: "visible",
    minHeight: 140,
    position: "relative",
    justifyContent: "center",
    alignItems: "center",
  },
  mediaImage: {
    width: "100%",
    height: 180,
    borderRadius: 7,
  },
  placeholderBox: {
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    width: "100%",
  },
  placeholderTitle: {
    fontSize: 12,
    fontWeight: "600",
    marginTop: 6,
    textAlign: "center",
  },
  placeholderSize: {
    fontSize: 11,
    opacity: 0.6,
    marginTop: 2,
  },
  videoBadge: {
    position: "absolute",
    bottom: 8,
    left: 8,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    zIndex: 5,
  },
  videoBadgeText: {
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: "700",
  },
  resizeFloatingBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 3,
    flexDirection: "row",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 6,
    zIndex: 20,
  },
  resizeFloatingBadgeText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontWeight: "800",
  },
  rightBorderHandle: {
    position: "absolute",
    right: -8,
    top: 0,
    bottom: 0,
    width: 16,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 15,
    ...Platform.select({
      web: {
        cursor: "ew-resize",
        userSelect: "none",
      } as any,
      default: {},
    }),
  },
  rightBorderHandleActive: {
    opacity: 1,
  },
  borderGripPill: {
    width: 5,
    height: 38,
    borderRadius: 2.5,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 3,
  },
  bottomBorderHandle: {
    position: "absolute",
    bottom: -8,
    left: "25%",
    right: "25%",
    height: 16,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 15,
    ...Platform.select({
      web: {
        cursor: "ns-resize",
        userSelect: "none",
      } as any,
      default: {},
    }),
  },
  bottomBorderHandleActive: {
    opacity: 1,
  },
  bottomGripPill: {
    width: 38,
    height: 5,
    borderRadius: 2.5,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 3,
  },
  cornerResizeHandle: {
    position: "absolute",
    right: -10,
    bottom: -10,
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 3,
    elevation: 6,
    zIndex: 16,
    ...Platform.select({
      web: {
        cursor: "nwse-resize",
        userSelect: "none",
      } as any,
      default: {},
    }),
  },
});
